import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import Flag from "../Flag/Flag";
import { BANNED_COUNTRY_CODES, countryName } from "../../utilities/country";
import { usd } from "../../utilities/format";
import { t } from "../../i18n/i18n";
import { useGetCountryPayoutQuery } from "../../store/api/payoutsApi";
import "./AuthSlider.scss";

const HOVER_INTENT = 220;

const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const readPayout = (data, country) => {
  const rows = Array.isArray(data?.results) ? data.results : [];
  const wanted = String(country ?? '').trim().toLowerCase();
  const row =
    rows.find((r) => String(r?.country ?? '').trim().toLowerCase() === wanted) ??
    (rows.length === 1 ? rows[0] : null);

  const share = (key) => ({
    total: asNumber(row?.[key]?.total) ?? asNumber(data?.totals?.[key]) ?? 0,
    count: asNumber(row?.[key]?.count) ?? 0,
  });

  return {
    // `??` and not `||`: a real 0.0 is an answer, not a missing one.
    total: asNumber(row?.total) ?? asNumber(data?.totals?.total),
    count: asNumber(row?.count) ?? asNumber(data?.count),
    profit: share('profit_share'),
    affiliate: share('affiliate_share'),
  };
};

const SPLIT_KINDS = [
  { id: 'profit', label: 'Profit share' },
  { id: 'affiliate', label: 'Affiliate' },
];

const splitsOf = (payout) =>
  !(payout.total > 0)
    ? []
    : SPLIT_KINDS.map(({ id, label }) => ({
        id,
        label,
        total: payout[id].total,
        count: payout[id].count,
        pct: Math.max(0, Math.min(100, (payout[id].total / payout.total) * 100)),
      }));

// Three distinct sets of countries to make the slide transition visually visible
const FLAG_CODES_1 = [
  "AT",
  "AL",
  "RO",
  "BE",
  "GB",
  "BG",
  "BA",
  "VA",
  "EE",
  "IS",
  "XK",
  "LV",
  "LT",
  "HR",
  "CZ",
  "DK",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LU",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "SK",
  "SI",
  "ES",
];

const FLAG_CODES_2 = [
  "US",
  "CA",
  "MX",
  "BR",
  "AR",
  "CO",
  "PE",
  "BB",
  "CL",
  "EC",
  "GT",
  "AG",
  "BO",
  "DO",
  "HN",
  "PY",
  "SV",
  "NI",
  "CR",
  "PA",
  "UY",
  "JM",
  "TT",
  "GY",
  "SR",
  "BS",
  "BZ",
  "GD",
  "DM",
  "LC",
  "VC",
  "KN",
];

const FLAG_CODES_3 = [
  "IN",
  "JP",
  "KR",
  "CN",
  "SG",
  "HK",
  "AU",
  "NZ",
  "AE",
  "SA",
  "ZA",
  "EG",
  "MA",
  "NG",
  "KE",
  "TR",
  "MY",
  "TH",
  "ID",
  "PH",
  "TW",
  "PK",
  "BD",
  "LK",
  "IL",
  "QA",
  "KW",
  "OM",
  "BH",
  "JO",
  "LB",
  "NP",
];

/**
 * Only countries we serve: the login flags (and the payout each one shows on
 * hover) leave out the same BANNED_COUNTRY_CODES the sign-up Country list does
 * (TI review F17 — Belarus, Venezuela, Cuba, Vietnam and Yemen were showing).
 * The lists above hold none of them; the filter keeps a future ban off the
 * slider without editing them.
 */
const served = (codes) => codes.filter((code) => !BANNED_COUNTRY_CODES.has(code));

const SLIDES = [
  {
    id: 1,
    title: "140+",
    subtitle: "Countries",
    flags: served(FLAG_CODES_1),
  },
  {
    id: 2,
    title: "140+",
    subtitle: "Countries",
    flags: served(FLAG_CODES_2),
  },
  {
    id: 3,
    title: "140+",
    subtitle: "Countries",
    flags: served(FLAG_CODES_3),
  },
];

const SLIDE_DURATION = 5000; // 5 seconds per slide

/** Room kept between the panel and the viewport's clipped edges, in px. */
const TIP_EDGE = 8;
/** The share of the viewport's width each side fades out under its mask. */
const VIEWPORT_FADE = 0.08;

export default function AuthSlider() {
  /** The flag the pointer has settled on, as an ISO code. */
  const [hovered, setHovered] = useState(null);
  const hoverTimer = useRef(null);
  // True while the pointer (or focus) is on any flag — read by the slide timer
  // below, so the slide holds still while someone is reading a country.
  const pausedRef = useRef(false);
  /**
   * Where the panel sits. It opens centred under its flag, then — once it is
   * drawn and its size is known — is slid sideways to stay inside the
   * viewport (which clips its overflow for the slide transition, and fades its
   * outer 8% under a mask), and flipped above the flag when there is no room
   * under it (the lower rows). `shift` moves the panel, and the nose moves the
   * other way, so it still points at the flag.
   */
  const tipRef = useRef(null);
  const [tipPlace, setTipPlace] = useState({ shift: 0, nose: 0, above: false });

  const openCountry = (code) => {
    pausedRef.current = true;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setTipPlace({ shift: 0, nose: 0, above: false });
      setHovered(code);
    }, HOVER_INTENT);
  };
  const closeCountry = () => {
    pausedRef.current = false;
    clearTimeout(hoverTimer.current);
    setHovered(null);
  };
  // A pending open must not land after the panel has gone.
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  /**
   * One query, re-keyed as the pointer moves. RTK Query caches per country, so
   * going back to a flag already looked at costs nothing, and `skip` means
   * nothing is asked for until a flag is actually settled on.
   */
  const hoveredName = hovered ? countryName(hovered) : null;
  const payout = useGetCountryPayoutQuery(hoveredName, { skip: !hoveredName });
  const figures = readPayout(payout.data, hoveredName);
  const { total, count } = figures;
  const splits = splitsOf(figures);

  // Measured before paint, and again when the figures land (they change the
  // panel's size): unshifted rect, clamp into the viewport, flip if needed.
  useLayoutEffect(() => {
    const tip = tipRef.current;
    if (!hovered || !tip) return;
    const viewportEl = tip.closest('.auth-slider__viewport');
    const tile = tip.parentElement;
    if (!viewportEl || !tile) return;
    const vp = viewportEl.getBoundingClientRect();
    const tileRect = tile.getBoundingClientRect();
    const rect = tip.getBoundingClientRect();

    // Where the panel would be with no shift, from where it is now.
    const baseLeft = rect.left - tipPlace.shift;
    const inset = vp.width * VIEWPORT_FADE + TIP_EDGE;
    const minLeft = vp.left + inset;
    const maxRight = vp.right - inset;
    let shift = 0;
    if (baseLeft < minLeft) shift = minLeft - baseLeft;
    else if (baseLeft + rect.width > maxRight) shift = maxRight - (baseLeft + rect.width);
    // The panel always comes fully inside; the nose follows the flag as far as
    // the panel's own edge allows (a flag half off-screen can't be pointed at
    // past the panel's corner).
    const maxNose = rect.width / 2 - 14;
    const nose = Math.max(-maxNose, Math.min(maxNose, -shift));

    const gap = 14;
    const roomBelow = vp.bottom - tileRect.bottom;
    const roomAbove = tileRect.top - vp.top;
    const above = roomBelow < rect.height + gap + TIP_EDGE && roomAbove > roomBelow;

    if (Math.round(shift) !== Math.round(tipPlace.shift) || above !== tipPlace.above) {
      setTipPlace({ shift, nose, above });
    }
  }, [hovered, payout.isFetching, total, tipPlace]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const progressInterval = useRef(null);
  const startTime = useRef(Date.now());

  useEffect(() => {
    // Reset progress when index changes
    setProgress(0);
    startTime.current = Date.now();

    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    let lastTick = Date.now();
    progressInterval.current = setInterval(() => {
      const now = Date.now();
      // Hovering a flag holds the slide: the paused time is added back onto
      // the start, so the bar resumes where it stopped instead of jumping.
      if (pausedRef.current) {
        startTime.current += now - lastTick;
        lastTick = now;
        return;
      }
      lastTick = now;
      const elapsed = now - startTime.current;
      const pct = Math.min((elapsed / SLIDE_DURATION) * 100, 100);
      setProgress(pct);

      if (elapsed >= SLIDE_DURATION) {
        setActiveIdx((prev) => (prev + 1) % SLIDES.length);
      }
    }, 50);

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [activeIdx]);

  const handleIndicatorClick = (idx) => {
    setActiveIdx(idx);
  };

  return (
    <div className="auth-slider">
      {/* Top Slide Indicators / Progress Bars */}
      <div className="auth-slider__indicators">
        {SLIDES.map((slide, idx) => {
          let fillWidth = "0%";
          if (idx < activeIdx) {
            fillWidth = "100%";
          } else if (idx === activeIdx) {
            fillWidth = `${progress}%`;
          }

          return (
            <div
              key={slide.id}
              className={`auth-slider__indicator-track ${idx === activeIdx ? "is-active" : ""}`}
              onClick={() => handleIndicatorClick(idx)}
            >
              <div
                className="auth-slider__indicator-fill"
                style={{ width: fillWidth }}
              />
            </div>
          );
        })}
      </div>

      {/* Horizontal Viewport and Sliding Track */}
      <div className="auth-slider__viewport">
        <div
          className="auth-slider__track"
          style={{
            transform: `translateX(-${activeIdx * (100 / SLIDES.length)}%)`,
          }}
        >
          {SLIDES.map((slide, slideIdx) => {
            // Slice the 32 flags into 4 rows of 8 flags to enable staggered flex styling
            const rows = [
              slide.flags.slice(0, 8),
              slide.flags.slice(8, 16),
              slide.flags.slice(16, 24),
              slide.flags.slice(24, 32)
            ];

            return (
              <div key={slide.id} className="auth-slider__slide">
                {/* Countries Flag Grid */}
                <div className="auth-slider__flags-grid">
                  {rows.map((rowFlags, rowIdx) => (
                    <div key={rowIdx} className="auth-slider__flags-row">
                      {rowFlags.map((code, flagIdx) => {
                        const globalIdx = rowIdx * 8 + flagIdx;
                        return (
                          <div
                            key={flagIdx}
                            // `is-open` lifts the flag whose panel is showing
                            // over its neighbours, so they can't cover it.
                            className={`auth-slider__flag-item${hovered === code ? ' is-open' : ''}`}
                            style={{
                              animationDelay: `${globalIdx * 0.02}s`,
                              // Only trigger staggered animation when slide is active
                              animationPlayState:
                                activeIdx === slideIdx ? "running" : "paused",
                            }}
                            onMouseEnter={() => openCountry(code)}
                            onMouseLeave={closeCountry}
                            tabIndex={0}
                            onFocus={() => openCountry(code)}
                            onBlur={closeCountry}
                          >
                            {/* The tile itself, inside the animated wrapper.
                                The entry animation owns the wrapper's
                                `transform` (`forwards` keeps its last keyframe
                                applied, which would outrank any hover rule), so
                                the hover lives on this inner box where it is
                                free to move. */}
                            <span className="auth-slider__flag-tile">
                              <Flag code={code} className="auth-slider__flag-img" />
                            </span>

                            {hovered === code && (
                              <span
                                ref={tipRef}
                                className={`auth-slider__flag-tip${tipPlace.above ? ' is-above' : ''}`}
                                style={{
                                  '--tip-shift': `${tipPlace.shift}px`,
                                  '--tip-nose': `${tipPlace.nose}px`,
                                }}
                                role="status"
                              >
                                <span className="auth-slider__flag-tip-name">
                                  {t(countryName(code))}
                                </span>
                                <span className="auth-slider__flag-tip-value">
                                  {payout.isFetching
                                    ? t('Loading…')
                                    : total == null
                                      ? t('Data not found')
                                      : total === 0
                                        ? t('No payouts yet')
                                        : t('{{amount}} paid out', { amount: usd(total) })}
                                </span>
                                {/* The number of payouts behind the figure —
                                    only where there is a figure to qualify. */}
                                {!payout.isFetching && total > 0 && count > 0 && (
                                  <span className="auth-slider__flag-tip-count">
                                    {count === 1
                                      ? t('across {{count}} payout', { count: count.toLocaleString('en-US') })
                                      : t('across {{count}} payouts', { count: count.toLocaleString('en-US') })}
                                  </span>
                                )}

                                {/* How the total splits. The bar carries the
                                    proportion at a glance and the rows carry
                                    the figures — the bar alone cannot be read
                                    off precisely, and the rows alone do not
                                    show which kind dominates. */}
                                {!payout.isFetching && splits.length > 0 && (
                                  <span className="auth-slider__flag-tip-split">
                                    <span
                                      className="auth-slider__flag-tip-bar"
                                      role="img"
                                      aria-label={splits
                                        .map((s) => `${t(s.label)} ${Math.round(s.pct)}%`)
                                        .join(', ')}
                                    >
                                      {splits
                                        .filter((s) => s.pct > 0)
                                        .map((s) => (
                                          <span
                                            key={s.id}
                                            className={`auth-slider__flag-tip-seg is-${s.id}`}
                                            style={{ width: `${s.pct}%` }}
                                          />
                                        ))}
                                    </span>

                                    {splits.map((s) => (
                                      <span
                                        className="auth-slider__flag-tip-row"
                                        key={s.id}
                                      >
                                        <span
                                          className={`auth-slider__flag-tip-dot is-${s.id}`}
                                          aria-hidden="true"
                                        />
                                        <span className="auth-slider__flag-tip-label">
                                          {t(s.label)}
                                        </span>
                                        <span className="auth-slider__flag-tip-amount">
                                          {usd(s.total)}
                                        </span>
                                      </span>
                                    ))}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Texts (Static, does not slide) */}
      <div className="auth-slider__text-box">
        <h2 className="auth-slider__title">140+</h2>
        <h3 className="auth-slider__subtitle">{t('Countries')}</h3>
      </div>
    </div>
  );
}
